import React, { useMemo, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useTrends } from "./useRunData";
import CircleStat from "./CircleStat";
import { useChartTheme, rateChartOptions } from "./chartTheme";
import { pct, Delta, runLabel } from "./format";
import { PendingRow, PendingInline } from "./Pending";
import "./TabOverview.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// What moved between the two most recent completed runs, from the trends slices.
function computeChanges(latest, prev) {
  if (!latest || !prev) return null;
  const prevPrompts = new Map((prev.byPrompt || []).map((p) => [p.promptNumber, p]));
  let gained = 0, lost = 0;
  for (const p of latest.byPrompt || []) {
    const b = prevPrompts.get(p.promptNumber);
    if (!b) continue;
    if (b.mentions === 0 && p.mentions > 0) gained += 1;
    else if (b.mentions > 0 && p.mentions === 0) lost += 1;
  }
  const prevComps = new Map((prev.allCompetitors || []).map((c) => [c.name, c.count]));
  const rising = (latest.allCompetitors || [])
    .map((c) => ({ name: c.name, delta: c.count - (prevComps.get(c.name) || 0) }))
    .filter((c) => c.delta > 0)
    .sort((a, b) => b.delta - a.delta)
    .slice(0, 2);
  const prevDomains = new Set(prev.domains || []);
  const newSources = (latest.domains || []).filter((d) => !prevDomains.has(d)).length;
  return { gained, lost, rising, newSources };
}

export default function TabOverview() {
  const { report, reportId } = useOutletContext();
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const theme = useChartTheme(chartRef);

  const runs = report.runs || [];
  const completed = runs.filter((r) => r.status === "completed");
  const latest = completed[completed.length - 1] || {};
  const previous = completed[completed.length - 2];

  const { runs: trend, pending: trendPending } = useTrends(reportId, report.updatedAt);
  const latestTrend = trend[trend.length - 1];
  const changes = useMemo(() => computeChanges(latestTrend, trend[trend.length - 2]), [trend, latestTrend]);

  const chartData = useMemo(() => ({
    labels: completed.map(runLabel),
    datasets: [
      { label: "Mention rate", data: completed.map((r) => Math.round((r.clientMentionRate || 0) * 100)),
        borderColor: "#2563eb", backgroundColor: "#2563eb", tension: 0.2 },
      { label: "Link rate", data: completed.map((r) => Math.round((r.clientLinkRate || 0) * 100)),
        borderColor: "#10b981", backgroundColor: "#10b981", tension: 0.2 },
    ],
  }), [completed]);
  const chartOptions = useMemo(() => rateChartOptions(theme), [theme]);

  return (
    <div className="tab-overview">
      <section className="overview-rates">
        <CircleStat label="Mentions (latest)" rate={latest.clientMentionRate || 0} />
        <CircleStat label="Links (latest)" rate={latest.clientLinkRate || 0} />
        <div className="overview-meta">
          <div><strong>{report.prompts?.length || 0}</strong> prompts</div>
          <div><strong>{report.models?.length || 0}</strong> models</div>
          <div><strong>{report.runsPerPrompt}</strong> runs/prompt</div>
          <div><strong>{completed.length}</strong> completed runs</div>
        </div>
        {previous && (
          <div className="overview-changes">
            <div className="overview-changes-title">Since run #{previous.runNumber}</div>
            <div>Mentions <Delta value={(latest.clientMentionRate || 0) - (previous.clientMentionRate || 0)} /></div>
            <div>Links <Delta value={(latest.clientLinkRate || 0) - (previous.clientLinkRate || 0)} /></div>
            {trendPending && <div className="muted">Prompts, competitors, sources <PendingInline /></div>}
            {changes && (
              <>
                <div>Prompts: {changes.gained} gained · {changes.lost} lost</div>
                {changes.rising.length > 0 && (
                  <div>Rising: {changes.rising.map((c) => `${c.name} (+${c.delta})`).join(", ")}</div>
                )}
                <div>{changes.newSources} new source domain{changes.newSources === 1 ? "" : "s"}</div>
              </>
            )}
          </div>
        )}
      </section>

      {completed.length > 0 && (
        <section className="overview-chart" ref={chartRef}>
          <h3>Visibility over time</h3>
          <div className="overview-chart-wrap">
            <Line data={chartData} options={chartOptions} />
          </div>
        </section>
      )}

      {completed.length > 0 && (trendPending || latestTrend?.byModel?.length > 0) && (
        <section className="overview-recent">
          <h3>By platform {latestTrend && <span className="muted">· run #{latestTrend.runNumber}</span>}</h3>
          <table className="runs-table">
            <thead><tr><th>Model</th><th>Responses</th><th>Mentions</th><th>Links</th></tr></thead>
            <tbody>
              {trendPending && <PendingRow cols={4} />}
              {!trendPending && (latestTrend?.byModel || []).map((m) => (
                <tr key={m.model}>
                  <td>{m.model}</td>
                  <td>{m.total}</td>
                  <td>{pct(m.mentionRate)}</td>
                  <td>{pct(m.linkRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="overview-recent">
        <h3>Recent runs</h3>
        <table className="runs-table">
          <thead><tr><th>#</th><th>Completed</th><th>Status</th><th>Mentions</th><th>Links</th><th>Sheet</th><th></th></tr></thead>
          <tbody>
            {runs.slice(-12).reverse().map((r) => (
              <tr key={r.runNumber}>
                <td>{r.runNumber}</td>
                <td>{r.completedAt ? new Date(r.completedAt).toLocaleString() : "—"}</td>
                <td>
                  {r.status === "error"
                    ? <span className="status-error" title={r.error || "Run failed"}>error</span>
                    : r.status}
                </td>
                <td>{r.status === "completed" ? pct(r.clientMentionRate) : "—"}</td>
                <td>{r.status === "completed" ? pct(r.clientLinkRate) : "—"}</td>
                <td>
                  {r.driveExport?.status === "ok" && (
                    <a href={r.driveExport.fileUrl} target="_blank" rel="noreferrer">Open</a>
                  )}
                  {r.driveExport?.status === "error" && (
                    <span className="status-error" title={r.driveExport.error}>Export failed</span>
                  )}
                </td>
                <td>
                  {r.status === "completed" && (
                    <button onClick={() => navigate(`/gpt-seo-tracking/reports/${reportId}/responses?run=${r.runNumber}`)}>
                      View
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
